/**
 * The companion conversation surface — her header (avatar + name + Artifacts
 * button) over the streaming thread and the composer. Presentational shell around
 * {@link ChatStream} + {@link Composer}; the Artifacts button opens the shared
 * gallery lightbox via `onOpenArtifacts` (wired by the parent shell).
 */
import type { Locale } from '@4f/i18n';
import { Avatar, Button, Icon, useT } from '@4f/ui';
import { type JSX, Show } from 'solid-js';
import type { CompanionView } from '../../api/types.ts';
import type { ChatStore } from '../../stores/chat.ts';
import { ChatStream } from './ChatStream.tsx';
import { Composer } from './Composer.tsx';
import type { StreamHandlers } from './StreamItemView.tsx';

export interface ChatViewProps {
  readonly store: ChatStore;
  readonly companion: CompanionView;
  /** Her resolved avatar URL (`avatarAssetId` → e2 URL), or `undefined` (initials fallback). */
  readonly avatarSrc?: string;
  readonly timeZone: string;
  readonly locale: Locale;
  /** Open the mobile switcher drawer (hamburger); absent on desktop. */
  readonly onOpenMenu?: () => void;
  /** Open the Artifacts gallery lightbox over her full set (the header button). */
  readonly onOpenArtifacts?: () => void;
  /** Open the shared lightbox at a clicked inline `gallery` directive. */
  readonly onOpenGallery?: StreamHandlers['onOpenGallery'];
  /** Open a modal surface from a `modal` directive (builder/settings/…). */
  readonly onOpenSurface?: StreamHandlers['onOpenSurface'];
  /** Resolve a `gallery` directive's asset ids to fetchable URLs. */
  readonly assetUrl?: StreamHandlers['assetUrl'];
}

export function ChatView(props: ChatViewProps): JSX.Element {
  const { t } = useT();

  return (
    <section class="f4-chat" aria-label={props.companion.name}>
      <header class="f4-chat__header">
        <Show when={props.onOpenMenu}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            class="f4-chat__menu"
            aria-label={t('chat.switcher.title')}
            onClick={() => props.onOpenMenu?.()}
          >
            <Icon name="menu" />
          </Button>
        </Show>
        <Avatar
          alt={props.companion.name}
          name={props.companion.name}
          src={props.avatarSrc}
          size="md"
        />
        <div class="f4-chat__identity">
          <h2 class="f4-chat__name">{props.companion.name}</h2>
          <Show when={props.companion.status === 'building'}>
            <span class="f4-chat__status">{t('surface.working')}</span>
          </Show>
        </div>
        <Show when={props.onOpenArtifacts}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            class="f4-chat__artifacts"
            onClick={() => props.onOpenArtifacts?.()}
          >
            {t('surface.artifacts')}
          </Button>
        </Show>
      </header>

      <ChatStream
        store={props.store}
        companion={props.companion}
        avatarSrc={props.avatarSrc}
        timeZone={props.timeZone}
        locale={props.locale}
        onOpenGallery={props.onOpenGallery}
        onOpenSurface={props.onOpenSurface}
        assetUrl={props.assetUrl}
      />

      <Composer store={props.store} companion={props.companion} />
    </section>
  );
}
